import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { Product } from '@/api/products';
import { cn } from '@/lib/utils';
import { useDirectSaleStore } from '@/stores/directSaleStore';
import { useDirectSaleMethod } from './useDirectSaleMethod';

const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary';

/**
 * The production type of the product — print or embroidery, say. One choice
 * covers every position. Hidden when there is nothing to choose, so a product
 * with a single method looks exactly as it did before the choice existed.
 *
 * Selecting the default method stores it like any other: the quote then
 * names it explicitly, which prices the same as no pick.
 */
export function DirectSaleMethodPicker({
  product,
  className,
}: {
  product: Product;
  className?: string;
}) {
  const { t } = useTranslation();
  const labelId = useId();
  const setDecorationMethod = useDirectSaleStore((s) => s.setDecorationMethod);
  const { methods, effectiveId } = useDirectSaleMethod(product);

  if (methods.length < 2) return null;

  // Nothing picked (or a pick the positions no longer offer) shows the
  // default — the first entry, default first — as the chosen one.
  const chosenId = effectiveId ?? methods[0]?.id;

  return (
    <div className={className}>
      <p
        id={labelId}
        className="text-[13px] uppercase tracking-wide text-ink-2"
      >
        {t('directSale.design.productionMethod')}
      </p>
      <div
        role="group"
        aria-labelledby={labelId}
        className="mt-2 flex flex-wrap gap-1.5"
      >
        {methods.map((method) => (
          <button
            key={method.id}
            type="button"
            aria-pressed={method.id === chosenId}
            onClick={() => setDecorationMethod(method.id)}
            className={cn(
              'min-h-9 rounded-control border-2 px-3 text-sm font-semibold text-ink transition-colors hover:bg-paper-2',
              focusRing,
              method.id === chosenId ? 'border-ink' : 'border-line'
            )}
          >
            {method.name}
          </button>
        ))}
      </div>
    </div>
  );
}
